import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@/shadcn-ui/popover';
import { cn } from '@/shadcn-ui/utils';
import { ReactNode, useEffect, useState } from 'react';

const eventPropagationStop = (
  e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>
) => {
  e.preventDefault();
  e.stopPropagation();
};

export interface PopoverContainerProps {
  /** NOTE: The trigger cannot have a React Fragment as the outermost element or else that will cause the popover functionality to break and not display the popover content at all. */
  triggerComponent: ReactNode;
  contents: ReactNode;
  hasArrow: boolean;
  contentAlign: 'center' | 'end' | 'start' | undefined;
  contentSide: 'top' | 'right' | 'bottom' | 'left' | undefined;
  sideOffset?: number;
  className?: string;
  arrowClassName?: string;
  /** When show is specified, the popover is converted into a controlled element */
  show?: boolean;
}

export default function PopoverContainer({
  triggerComponent,
  contents,
  hasArrow,
  contentAlign,
  contentSide,
  sideOffset,
  className,
  arrowClassName,
  show,
}: PopoverContainerProps) {
  const isControlled = show !== undefined;
  const [showPopover, setShowPopover] = useState(false);

  const popoverEventStart = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>
  ) => {
    eventPropagationStop(e);
    setShowPopover(true);
  };

  const popoverEventEnd = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>
  ) => {
    eventPropagationStop(e);
    setShowPopover(false);
  };

  /** Open / Close popover on touch start, meaning popover stays open until touched again */
  const popoverEventTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    eventPropagationStop(e);
    setShowPopover((old) => !old);
  };

  /** Hide popover on scroll, it does not reposition well as the screen moves */
  useEffect(() => {
    const handleScroll = () => setShowPopover(false);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Popover open={isControlled ? show : showPopover}>
      <PopoverTrigger asChild>
        {isControlled ? (
          triggerComponent
        ) : (
          <div
            onMouseEnter={popoverEventStart}
            onTouchStart={popoverEventTouchStart}
            onMouseLeave={popoverEventEnd}
            onTouchEnd={eventPropagationStop}
          >
            {triggerComponent}
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={contentAlign}
        side={contentSide}
        sideOffset={sideOffset}
        // This is needed to prevent the popover from adjusting it's position when it is scrolled off of the viewport.
        avoidCollisions={false}
        className={cn('rounded-[16px] bg-flexcar-neutral100 shadow-shadow30', className)}
      >
        {contents}
        {hasArrow ? <PopoverArrow className={arrowClassName} /> : null}
      </PopoverContent>
    </Popover>
  );
}
